import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../api";
import { useCart } from "../contexts/CartContext";
import useNoIndex from "../hooks/useNoIndex";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id"); // Stripe path
  const paypalToken = params.get("token");    // PayPal path — PayPal Order ID
  const paypalPayer = params.get("PayerID");
  const [state, setState] = useState({ status: "checking", payment_status: null, amount_total: null, currency: "usd" });
  useNoIndex();
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    // PayPal flow: token + PayerID present, no session_id
    if (paypalToken && !sessionId) {
      const capture = async () => {
        try {
          const { data } = await api.post(`/checkout/paypal/capture/${paypalToken}`);
          if (data.status === "COMPLETED" || data.ok) {
            clear();
            setState({ status: "complete", payment_status: "paid" });
          } else {
            setState({ status: "error", payment_status: data.status || "error" });
          }
        } catch (e) {
          setState({ status: "error", payment_status: "capture_failed" });
        }
      };
      capture();
      return;
    }

    // Stripe flow (legacy)
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing_session" });
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setState(data);
        if (data.payment_status === "paid") {
          clear();
          return;
        }
        if (data.status === "expired") return;
        if (attempts.current < 8) {
          attempts.current += 1;
          timer = setTimeout(poll, 2000);
        } else {
          setState((s) => ({ ...s, status: "timeout" }));
        }
      } catch {
        setState({ status: "error", payment_status: "error" });
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paypalToken]);

  const paid = state.payment_status === "paid";
  const failed = ["expired", "missing_session", "error", "capture_failed"].includes(state.payment_status) || state.status === "expired";

  return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <div className="surface p-12">
        {state.status === "checking" && !failed && !paid && (
          <>
            <Loader2 className="mx-auto text-[#00C3FF] animate-spin" size={36} />
            <div className="overline mt-6">VERIFYING TRANSACTION</div>
            <h1 className="font-heading text-3xl mt-3">Confirming your payment…</h1>
            <p className="text-sm text-[#8A94A6] mt-4">This usually takes just a few seconds.</p>
          </>
        )}

        {paid && (
          <>
            <CheckCircle2 className="mx-auto text-[#00C3FF]" size={44} />
            <div className="overline mt-6">ORDER CONFIRMED</div>
            <h1 className="font-heading text-4xl mt-3">Thank you for your order.</h1>
            <p className="text-sm text-[#8A94A6] mt-4 max-w-md mx-auto">
              Your payment has been received. A confirmation has been emailed to you. Your compounds
              will dispatch within 24 hours.
            </p>
            {state.amount_total ? (
              <div className="font-mono-tech text-2xl mt-6">${(state.amount_total / 100).toFixed(2)} {state.currency?.toUpperCase()}</div>
            ) : null}
            <Link to="/shop" className="btn-primary mt-8 inline-flex">Continue Browsing</Link>
          </>
        )}

        {failed && (
          <>
            <XCircle className="mx-auto text-red-400" size={40} />
            <div className="overline mt-6">TRANSACTION ISSUE</div>
            <h1 className="font-heading text-3xl mt-3">We couldn't confirm your payment.</h1>
            <p className="text-sm text-[#8A94A6] mt-4">Your card has not been charged. Please try again or contact support.</p>
            <Link to="/shop" className="btn-ghost mt-8 inline-flex">Return to Shop</Link>
          </>
        )}
      </div>
    </div>
  );
}
